import React, { useEffect, useMemo, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input, Text } from '@node-space/ui-components'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useBankDetailValidation } from 'pages/Contacts/hooks/useBankDetailsValidation'
import { useFormFieldOptions } from 'pages/Contacts/hooks/useFiatFormFieldOptions'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { getIsValidIban } from 'pages/Contacts/utils/fiatInstruments'
import {
  BeneficiaryDetailsCurrency,
  PaymentMethod,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'

type BankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
}

export const LocalBankDetails = ({
  formFieldLabels,
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
  trigger,
  setError,
}: BankDetailsProps) => {
  const { t } = useTranslation()
  const [accountNumber, setAccountNumber] = useState<string | undefined>(undefined)
  const [isRoutingNumberValid, setIsRoutingNumberValid] = useState<boolean>(false)

  const { accountTypeOptions, paymentMethodOptions } = useFormFieldOptions({ formValues })

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isUsdAchOrFedwire = isUsd && formValues?.paymentMethod !== USDBeneficiaryOptions.CUBIX

  const { setRequestError } = useMappedErrorMessage()

  const isIbanCurrency =
    formValues?.currency === BeneficiaryDetailsCurrency.EUR ||
    formValues?.currency === BeneficiaryDetailsCurrency.AED

  const { ibanBankDetails, isValidatingIban, isValidatingIbanError } = useBankDetailValidation({
    intlIban: isIbanCurrency && accountNumber,
    setRequestError,
  })

  const { scanBankDetails, isValidatingScan } = useBankDetailValidation({
    accountNumber: accountNumber,
    sortCode: formValues?.bankCode,
    setRequestError,
  })

  const {
    usBankDetails,
    isValidatingRoutingNumber,
    validateRoutingNumber,
    isValidatingRoutingNumberError,
  } = useBankDetailValidation({
    routingNumber: formValues?.bankCode,
    setRequestError,
  })

  const isBankDetailsValidationLoading = isValidatingIban || isValidatingScan

  const ibanSupportedPaymentMethods = ibanBankDetails?.supportedPaymentMethods
  const scanSupportedPaymentMethods = scanBankDetails?.supportedPaymentMethods
  const isGBP = formValues?.currency === BeneficiaryDetailsCurrency.GBP

  const isFPSandChapsSupported = useMemo(() => {
    return (
      scanSupportedPaymentMethods?.includes('CHAPS') ||
      scanSupportedPaymentMethods?.includes('BACS') ||
      scanSupportedPaymentMethods?.includes('FASTER_PAYMENT') ||
      scanSupportedPaymentMethods?.includes('CCC_PAYMENTS')
    )
  }, [scanSupportedPaymentMethods])

  useEffect(() => {
    if (scanBankDetails && !isFPSandChapsSupported && isGBP) {
      setError('accountNumber', { message: t('reachabilityCheckErrorMessage') })
    }
  }, [scanBankDetails])

  const isValidIban = useMemo(() => {
    return getIsValidIban({
      currency: formValues?.currency,
      ibanBankDetails,
      ibanSupportedPaymentMethods,
    })
  }, [formValues?.currency, ibanBankDetails, ibanSupportedPaymentMethods])

  useEffect(() => {
    if (isValidIban) {
      setValue('bankCode', ibanBankDetails?.bankAccount?.bankCode)
    } else {
      setValue('bankCode', '')
    }
  }, [ibanBankDetails?.bankAccount?.bankCode, isValidIban])

  useEffect(() => {
    clearErrors(['accountNumber'])

    if (!isBankDetailsValidationLoading) {
      const hasValidationFailed = ibanBankDetails?.validationFailed?.length >= 1
      const isInvalidIban = hasValidationFailed || !isValidIban

      if (isInvalidIban) {
        setError('accountNumber', { message: t('ibanValidationError') })
      }
    }
  }, [ibanBankDetails, isValidatingIbanError, isValidIban, isBankDetailsValidationLoading])

  useEffect(() => {
    // Payment methods are dynamic based on capabilities, so set the first one on form load
    if (!formValues?.paymentMethod && paymentMethodOptions?.length) {
      setValue('paymentMethod', paymentMethodOptions?.[0]?.value)
    }
  }, [paymentMethodOptions, formValues?.paymentMethod])

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, formValues?.paymentMethod, clearErrors])

  const handleInputChange =
    (name: keyof ContactFiatInstrumentFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(name, e.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const onAccountTypeChange = (value: string) => {
    setValue('accountType', value)
  }

  const handleAccountNumberOnBlur = async () => {
    clearErrors()
    const isValid = await trigger('accountNumber')

    if (isValid && formValues?.accountNumber) {
      setAccountNumber(formValues.accountNumber)
    }
  }

  const handleOnRoutingNumberBlur = async () => {
    clearErrors()

    if (!formValues?.bankCode) return

    await validateRoutingNumber()

    const isValid =
      !!usBankDetails?.bankDetails?.bankAddress && !usBankDetails?.validationFailed?.length
    setIsRoutingNumberValid(isValid)

    if (!isValid) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
      resetBankAddress()
    } else {
      const { addressLine1, addressLine2, city, region, postCode } =
        usBankDetails?.bankDetails?.bankAddress || {}
      setValue('beneficiaryAddressLine1', addressLine1)
      setValue('beneficiaryAddressLine2', addressLine2)
      setValue('beneficiaryCity', city)
      setValue('beneficiaryRegion', region)
      setValue('beneficiaryPostCode', postCode)
    }
  }

  const resetBankAddress = () => {
    setValue('bankName', '')
    setValue('bankCode', '')
    setValue('bankAddressLine1', '')
    setValue('bankAddressLine2', '')
    setValue('bankCity', '')
    setValue('bankRegion', '')
    setValue('bankPostCode', '')
  }

  const isAccountNumberAndBankCodeInline =
    formValues?.currency === BeneficiaryDetailsCurrency.EUR ||
    formValues?.currency === BeneficiaryDetailsCurrency.GBP

  const onBeneficiaryTypeSelection = (type: PaymentMethod) => {
    setValue('paymentMethod', type)
    setValue('accountNumber', '')
    setValue('accountNumberConfirm', '')
    resetBankAddress()
  }

  useEffect(() => {
    if (isValidatingRoutingNumberError) {
      setError('bankCode', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError])

  return (
    <Box width="full" paddingT={12}>
      <Text variant="heading-2">{t('formFields.bankDetails')}</Text>
      <Box flexCol width="full" gapY={16} paddingT={12}>
        {isUsd && (
          <Combobox
            {...register('paymentMethod')}
            id="paymentMethod"
            key="paymentMethod"
            testId="payment-method"
            label={t('manageBeneficiaries.selectPaymentMethod')}
            defaultValue={formValues?.paymentMethod ?? paymentMethodOptions?.[0]?.value}
            onChange={onBeneficiaryTypeSelection}
            items={paymentMethodOptions}
            disabled={paymentMethodOptions?.length === 1}
          />
        )}

        <Box
          gapX={12}
          gapY={16}
          paddingB={12}
          width="full"
          flex={isAccountNumberAndBankCodeInline}
          flexCol={!isAccountNumberAndBankCodeInline}
        >
          <Box
            {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full', gapX: 12 })}
          >
            <Input
              {...register('accountNumber')}
              name="accountNumber"
              testId="accountNumber"
              label={formFieldLabels?.accountNumber}
              placeholder={formFieldLabels?.accountNumberPlaceholder}
              errorText={errors?.accountNumber?.message}
              error={!!errors?.accountNumber?.message}
              onBlur={handleAccountNumberOnBlur}
              onChange={handleInputChange('accountNumber')}
              loading={isBankDetailsValidationLoading}
              IconButton={
                isValidIban && <Button variant="ghost" iconElement={<Icon name="Check" />} />
              }
              iconPosition="right"
            />
          </Box>

          {isUsdAchOrFedwire && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <Input
                {...register('accountNumberConfirm')}
                name="accountNumberConfirm"
                testId="accountNumberConfirm"
                label={t('formFields.confirmAccountNumber')}
                placeholder={t('formFields.placeholder.confirmAccountNumber')}
                errorText={errors?.accountNumberConfirm?.message}
                error={!!errors?.accountNumberConfirm?.message}
                onChange={handleInputChange('accountNumberConfirm')}
              />
            </Box>
          )}

          <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
            <Input
              {...register('bankCode')}
              name="bankCode"
              testId="bankCode"
              label={formFieldLabels.bankCode}
              placeholder={formFieldLabels?.bankCodePlaceholder}
              errorText={errors?.bankCode?.message}
              error={!!errors?.bankCode?.message}
              loading={isValidatingRoutingNumber}
              onChange={handleInputChange('bankCode')}
              onBlur={handleOnRoutingNumberBlur}
              IconButton={
                isRoutingNumberValid &&
                formValues?.routingNumber && (
                  <Button variant="ghost" iconElement={<Icon name="Check" />} />
                )
              }
              iconPosition="right"
            />
          </Box>
          {isUsdAchOrFedwire && (
            <Combobox
              {...register('accountType')}
              id="accountType"
              testId="accountType"
              label={t('formFields.accountType')}
              items={accountTypeOptions}
              errorText={errors?.accountType?.message}
              onChange={value => {
                onAccountTypeChange(value)
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
